import clsx from "clsx";
import {useField, useFormikContext} from "formik";
import {t} from "i18next";
import PropTypes from "prop-types";

import {useDebouncedCallback} from "../../hook/debounced";
import {COLOR_SCHEME} from "../../utilities/colorSheme";
import QuestionItem from "../../utilities/QuestionItem";
import ToolTip from "../../utilities/ToolTip";
import useRoutePermissions, {
  UI_PERMISSION_TYPE,
} from "../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";

import useInputsTooltip from "./hook/InputsTooltipService";

const TextInput = ({
  label,
  width = "100%",
  className = "",
  generalClassName = "",
  inputClassName = "",
  questionItemClassName = "",
  question = null,
  func = null,
  autolabel,
  labelWrap,
  labelStyles,
  handleInputChange = () => {},
  onBlur = () => {},
  disabled = false,
  placeholder = "",
  hasTitle = false,
  setref,
  style,
  required = false, //adds * to label text
  accentText = false, //show input (not label) in alert color
  tooltipMessage = null,
  bottomTooltip = false,
  noFunctional = false,
  grow = false,
  value, //for controlled component usage
  afterLabelText = "", //text between label and input field
  clearedValue = false, //remove all spaces in field value
  isRequiredAccented = true,
  hidden = false,
  uiPermissionType = UI_PERMISSION_TYPE.WRITABLE,
  ...props
}) => {
  const {isDisabledByRoutePermission} = useRoutePermissions({uiPermissionType});

  const [field, meta] = useField(props);
  const {tooltipShown, onMouseEnter, onMouseLeave, setTooltipShown} = useInputsTooltip(field);

  const {submitCount, setFieldValue} = useFormikContext();

  const trimValue = v => (typeof v === "string" ? v.trim() : v);

  const funcDebounce = useDebouncedCallback(value => {
    if (func !== null) func(value);
  }, 1000);

  const clearValue = value => value.trim().replace(/\s+/g, "");

  const onChange = e => {
    let value = e.target.value;

    if (clearedValue) {
      value = clearValue(value);
    }

    setFieldValue(field.name, value);
    handleInputChange({...e, target: {...e.target, value}});
    if (value.length > 0) {
      setTooltipShown(false);
    }
    if (func !== null) {
      funcDebounce(value);
    }
  };

  const onBlurHandler = e => {
    const trimmed = trimValue(e?.target?.value);
    if (trimmed !== e?.target?.value) {
      setFieldValue(field.name, trimmed);
    }

    if (typeof field?.onBlur === "function") {
      field.onBlur(e);
    }

    onBlur(e, trimmed, setFieldValue);
  };
  // useEffect(() => {
  //   if (func !== null && (!field?.value || field?.value === "")) {
  //     funcDebounce(null);
  //   }
  // }, [field?.value]);

  const labelText = label ? t(label) : "";
  const isRequiredMarkHighlighted =
    !field.value && !disabled && !noFunctional && !props.readOnly && !isDisabledByRoutePermission;

  const fieldValue = clearedValue ? clearValue(field.value || value || "") : field.value || value || "";

  return (
    <ToolTip
      className={clsx(
        "project_input aibl",
        generalClassName,
        labelWrap ? "" : "labelWrap",
        grow && "fGrow",
        hidden && "hiddenEl",
      )}
      style={{...(style || {})}}
      title={
        noFunctional
          ? "No functional"
          : isDisabledByRoutePermission && !props.readOnly && !disabled
          ? "Your position has no permission for this action"
          : tooltipMessage
      }
      bottom={bottomTooltip}
    >
      <>
        {label && (
          <label
            htmlFor={props.name}
            style={{...labelStyles, whiteSpace: "pre"}}
            className={autolabel ? "autolabel" : ""}
          >
            {/* { typeof label === "string" ? t(label) : label }  */}
            <span>
              {labelText}
              {required && (
                <span className={isRequiredAccented && isRequiredMarkHighlighted ? "required-asterisk" : ""}>*</span>
              )}
              :
            </span>
          </label>
        )}
        <div
          className={`${className} project_input_container row aibl`}
          style={{
            width,
            position: "relative",
            ...(grow ? {flex: "1 1 auto", minWidth: 0} : {}),
          }}
        >
          {afterLabelText && <span className="pl5">{t(afterLabelText)}</span>}
          {/* <div className={className}> */}
          <input
            {...props}
            {...field}
            className={clsx("fGrow", inputClassName)}
            disabled={isDisabledByRoutePermission || disabled || noFunctional}
            autoComplete="off"
            placeholder={typeof placeholder === "string" ? t(placeholder) : placeholder}
            value={fieldValue}
            onChange={onChange}
            onBlur={onBlurHandler}
            onMouseEnter={question ? onMouseEnter : null}
            onMouseLeave={question ? onMouseLeave : null}
            title={hasTitle && field.value ? field.value : null}
            ref={setref}
            style={{
              ...(afterLabelText ? {paddingLeft: "1px"} : {}),
              ...(props.readOnly || disabled || noFunctional || isDisabledByRoutePermission
                ? {cursor: "default"}
                : {background: "white"}),
              ...(accentText ? {color: COLOR_SCHEME.ALERT} : {}),
            }}
          />
          {/* </div> */}
          {meta.touched && meta.error && submitCount > 0 ? <div className="error">{meta.error}</div> : null}
        </div>
        {question ? (
          <QuestionItem title={question} tooltipShown={tooltipShown} className={questionItemClassName} />
        ) : null}
      </>
    </ToolTip>
  );
};

TextInput.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  width: PropTypes.string,
  className: PropTypes.string,
  question: PropTypes.string,
  func: PropTypes.func,
  autolabel: PropTypes.bool,
  labelStyles: PropTypes.object,
  handleInputChange: PropTypes.func,
  generalClassName: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  hasTitle: PropTypes.bool,
  props: PropTypes.array,
  noFunctional: PropTypes.bool,
  afterLabelText: PropTypes.string,
  clearedValue: PropTypes.bool,
};

export default TextInput;
